import "./index.css"
import React, { forwardRef, memo, useId, useMemo, useState, useRef, useCallback, useEffect } from "react";
import { FormGroup } from "react-bootstrap";
import {
  FormLabel,
  FormFeedback,
  DropIcon,
  LoadingMessage,
  NoOptionsMessage,
  ClearIndicator,
  MenuList,
  MultiValueRemove,
} from "./utils";

import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";

/* ----------------------------------------------
   Default Styles & Classes
---------------------------------------------- */
const customStyle = {
  control: (base, state = {}) => {
    const isMultiValue = !!state.isMulti && state.getValue?.().length > 0;
    return {
      ...base,
      minHeight: 32,
      minWidth: 54,
      padding: isMultiValue ? "8px 14px" : "8px 14px",
      borderColor: state.isFocused || state.menuIsOpen ? "var(--border)" : "var(--border-100)",
      boxShadow: state.isFocused ? "0 0 0 1px var(--primary)" : "none",
      "&:hover": {
        borderColor: state.isFocused || state.menuIsOpen ? "var(--primary)" : "var(--border)",
      },
    };
  },
  menuPortal: (base) => ({
    ...base,
    zIndex: 1000,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 1000,
  }),
  loadingIndicator: (base) => ({
    ...base,
    color: "var(--primary)",
    padding: "0 6px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "160px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--primary)"
      : state.isFocused
        ? "var(--primary-100)"
        : "transparent",
    color: state.isSelected
      ? "var(--white)"
      : state.isFocused
        ? "var(--primary)"
        : "var(--text-primary)",
    "&:active": {
      backgroundColor: "var(--primary)",
      color: "var(--white)",
    },
    cursor: "pointer",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--primary-100)",
    borderColor: "var(--border)",
    padding: "5px 6px"
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--primary)",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--primary)",
    "&:hover": {
      backgroundColor: "var(--primary)",
      color: "var(--white)",
    },
  }),
};

const customClasses = {
  control: (state) =>
    ["rounded-1 shadow-none form-control",
      state.isDisabled ? "disabled" : "cursor-pointer",
    ].join(" "),
  menu: () => "bg-white border shadow-sm p-2 pe-2 rounded-2",
  menuList: () => "p-0 position-relative custom-scroll",
  option: (state) =>
    [
      "text-start fs-sm px-2 py-1 rounded-1 cursor-pointer select-option",
      state.isSelected ? "active" : "",
      state.isFocused ? "focused" : "",
    ].join(" "),
  placeholder: () => "text-base text-secondary",
  valueContainer: ({ isMulti, hasValue }) =>
    `p-0 m-0 ${isMulti && hasValue ? "d-flex gap-2" : ""}`,
  singleValue: () => "m-0",
  multiValue: ({ isMulti }) =>
    `primary-text rounded-1 gap-1 m-0 lh-1 align-items-center ${isMulti ? "flex-wrap" : "flex-nowrap"}`,
  multiValueLabel: () => "primary-text fs-xs fw-medium p-0",
  multiValueRemove: () => "p-0 text-danger bg-transparent transition-all",
  clearIndicator: () => "p-0",
  indicatorSeparator: () => "m-0",
  dropdownIndicator: () => "p-0",
  indicatorsContainer: () => "p-0 gap-2",
  input: () => "m-0 p-0",
  group: () => "p-0",
  groupHeading: () =>
    "text-secondary fw-bold fs-xs text-uppercase px-2 py-1 bg-white mb-1",
};




/* ----------------------------------------------
   Select Component Resolver (moved outside)
---------------------------------------------- */
const SelectComponent = memo((props) => {
  const { loadOptions, isCreatable } = props;
  let Component;

  if (loadOptions) {
    Component = isCreatable ? AsyncCreatableSelect : AsyncSelect;
  } else {
    Component = isCreatable ? CreatableSelect : Select;
  }

  return <Component {...props} />;
});

/* ----------------------------------------------
   Main Component
---------------------------------------------- */
const CustomSelect = (
  {
    isDisabled = false,
    className = "",
    coverClass = "",
    isHorizontal = false,
    style = {},
    label = {},
    validation = {},
    selectProps = {},
    isClearable = false,
    isSearchable = true,
    isLoading = false,
    onInputChange,
    defaultValue = null,
    value = null,
    onChange = () => { },
    options = [],
    menuPlacement = "auto",
    menuIsOpen,
    defaultOptions,
    cacheOptions,
    loadOptions,
    required = false,
    isCreatable = false,
    newOption = null,
    lookLikeInput = false,
    fetchOptions,
    dataMapper,
    showSelectAll = false,
    hideSelectedChips = false,
    initialLabel = "",
    
    // Standard react-select props passed directly
    selectClassName = "",
    styles = {},
    classNames = {},
    components = {},
    placeholder = "Select...",
    id,
    name,
    
    ...selectRest
  },
  ref,
) => {
  /* ----------------------------------------------
     Label props
  ---------------------------------------------- */
  const {
    name: labelName = "",
    id: labelId,
    isHidden = false,
    class: labelClass = "",
  } = label || {};

  /* ----------------------------------------------
     Validation props
  ---------------------------------------------- */
  const {
    isInvalid = false,
    message: errorMessage = "",
    className: validationClass = "",
    name: validationName = "",
  } = validation || {};

  /* ----------------------------------------------
     Select Props (Merged for backward compatibility)
  ---------------------------------------------- */
  const finalSelectClassName = selectClassName || selectProps.className || "";
  const finalSelectId = id || selectProps.id || "";
  const finalPlaceholder = selectProps.placeholder !== undefined ? selectProps.placeholder : placeholder;
  const finalSelectName = name || selectProps.name || validationName;

  /* ----------------------------------------------
     ID Resolver
  ---------------------------------------------- */
  const autoId = useId();
  const resolvedId = labelId || finalSelectId || autoId;

  /* ----------------------------------------------
     Memoized Overrides
  ---------------------------------------------- */
  const { icon, iconClass = "" } = selectRest;
  const mergedClassNames = useMemo(
    () => {
      const finalClassNamesOverride = { ...classNames, ...selectProps.classNames };
      return {
        ...customClasses,
        ...finalClassNamesOverride,
        control: (state) => {
          const baseFn = finalClassNamesOverride.control || customClasses.control;
          let classes = baseFn(state);
          if (icon) {
            classes = classes.replace("px-5", "ps-5");
          }
          return classes;
        },
      };
    },
    [classNames, selectProps.classNames, icon],
  );

  const mergedStyles = useMemo(
    () => {
      const finalStylesOverride = { ...styles, ...selectProps.styles };
      return {
        ...customStyle,
        ...finalStylesOverride,
        control: (base, state) => ({
          ...customStyle.control(base, state),
          ...(finalStylesOverride.control ? finalStylesOverride.control(base, state) : {}),
          paddingRight: icon ? "50px" : undefined,
        }),
      };
    },
    [styles, selectProps.styles, icon],
  );

  const resolvedName = finalSelectName || selectRest.name;

  const mergedComponents = useMemo(
    () => {
      const finalComponentsOverride = { ...components, ...selectProps.components };
      return {
        DropdownIndicator: lookLikeInput ? null : DropIcon,
        LoadingIndicator: null,
        LoadingMessage,
        NoOptionsMessage,
        ClearIndicator,
        MenuList,
        MultiValueRemove,
        ...finalComponentsOverride,
      };
    },
    [lookLikeInput, components, selectProps.components],
  );

  const resolvedValue = useMemo(() => {
    if (value === undefined || value === null) return null;

    const isMulti = selectRest.isMulti;

    const resolveSingle = (val) => {
      if (val && typeof val === "object" && "value" in val) {
        return val;
      }
      const found = options?.find((opt) => opt.value === val);
      if (found) return found;
      if (val !== null && val !== undefined && val !== "") {
        return { value: val, label: String(val) };
      }
      return null;
    };

    if (isMulti) {
      if (Array.isArray(value)) {
        return value.map(resolveSingle).filter(Boolean);
      }
      if (typeof value === "string") {
        return value.split(",").map(item => item.trim()).map(resolveSingle).filter(Boolean);
      }
      return [resolveSingle(value)].filter(Boolean);
    }

    return resolveSingle(value);
  }, [value, options, selectRest.isMulti]);


  const isSearchableMode = !!fetchOptions;

  const [sdOptions, setSdOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [sdIsLoading, setSdIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const searchTimeout = useRef(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const [selectedCache, setSelectedCache] = useState({});
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);

  const resolveDataMapper = useCallback(
    (data) => (dataMapper ? dataMapper(data) : data.map((item) => ({ value: item.id, label: item.name }))),
    [dataMapper]
  );

  const sdLoadOptions = useCallback(
    async (currentSearch, currentPage, isNewSearch = false) => {
      if (!isSearchableMode) return;
      if (isLoadingRef.current) return;
      if (!isNewSearch && !hasMoreRef.current) return;

      isLoadingRef.current = true;
      setSdIsLoading(true);
      try {
        const result = await fetchOptions(currentSearch, currentPage);
        const { data, meta } = result;

        const newOptions = resolveDataMapper(data);

        setSdOptions((prev) => (isNewSearch ? newOptions : [...prev, ...newOptions]));

        const more = meta.page < meta.pages;
        setHasMore(more);
        hasMoreRef.current = more;
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        isLoadingRef.current = false;
        setSdIsLoading(false);
      }
    },
    [fetchOptions, resolveDataMapper, isSearchableMode]
  );

  const handleSdInputChange = useCallback((newInputValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newInputValue);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);

      searchTimeout.current = setTimeout(() => {
        setSearch(newInputValue);
        setPage(1);
        setHasMore(true);
        hasMoreRef.current = true;
      }, 500);
    } else if (action === "menu-close") {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      setInputValue("");
      setSearch("");
      setPage(1);
      setHasMore(true);
      hasMoreRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (isSearchableMode) {
      sdLoadOptions(search, 1, true);
    }
  }, [search, sdLoadOptions, isSearchableMode]);

  useEffect(() => {
    if (isSearchableMode && page > 1) {
      sdLoadOptions(search, page, false);
    }
  }, [page, search, sdLoadOptions, isSearchableMode]);

  const handleMenuScrollToBottom = useCallback(() => {
    if (isSearchableMode && hasMore && !sdIsLoading) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, sdIsLoading, isSearchableMode]);

  useEffect(() => {
    if (isSearchableMode && isSelectAllMode && sdOptions.length > 0) {
      const currentSelectedValues = Array.isArray(value) ? value : [];
      const visibleValues = sdOptions
        .filter(opt => opt.value !== "all")
        .map(opt => opt.value);

      const missingValues = visibleValues.filter(v => !currentSelectedValues.includes(v));

      if (missingValues.length > 0) {
        onChange(resolvedName, [...currentSelectedValues, ...missingValues]);
      }
    }
  }, [sdOptions, isSelectAllMode, value, onChange, resolvedName, isSearchableMode]);

  useEffect(() => {
    if (!isSearchableMode || !sdOptions || sdOptions.length === 0) return;

    setSelectedCache(prev => {
      const newCache = { ...prev };
      let updated = false;

      const flatten = (items) => {
        items.forEach(item => {
          if (item.options) flatten(item.options);
          else if (item.value && item.label && prev[item.value] !== item.label) {
            newCache[item.value] = item.label;
            updated = true;
          }
        });
      };

      flatten(sdOptions);
      return updated ? newCache : prev;
    });
  }, [sdOptions, isSearchableMode]);

  const handleSdChange = useCallback((selected, actionMeta) => {
    const { action, option } = actionMeta;

    if (action === "select-option" && option?.value === "all") {
      const currentVisibleValues = sdOptions
        .filter(opt => opt.value !== "all")
        .map(opt => opt.value);

      const currentSelectedValues = Array.isArray(value) ? value : [];
      const allVisibleSelected = currentVisibleValues.every(val => currentSelectedValues.includes(val));

      if (allVisibleSelected && isSelectAllMode) {
        setIsSelectAllMode(false);
        onChange(resolvedName, []);
      } else {
        setIsSelectAllMode(true);
        const nextSet = new Set([...currentSelectedValues, ...currentVisibleValues]);
        onChange(resolvedName, Array.from(nextSet));
      }
      return;
    }

    if (action === "deselect-option" || action === "remove-value" || action === "pop-value") {
      setIsSelectAllMode(false);
    }

    if (selectRest.isMulti) {
      const selectedArray = selected || [];
      if (selectedArray.length > 0) {
        setSelectedCache(prev => {
          const newCache = { ...prev };
          let updated = false;
          selectedArray.forEach(s => {
            if (s.value && s.label && prev[s.value] !== s.label) {
              newCache[s.value] = s.label;
              updated = true;
            }
          });
          return updated ? newCache : prev;
        });
      }

      onChange(resolvedName, selectedArray.filter(s => s.value !== 'all').map(s => s.value));
    } else {
      onChange(resolvedName, selected ? selected.value : "");
    }
  }, [sdOptions, isSelectAllMode, value, onChange, resolvedName, selectRest.isMulti]);

  const finalOptions = useMemo(() => {
    if (!isSearchableMode) return options;
    if (selectRest.isMulti && showSelectAll && sdOptions.length > 0) {
      return [{ value: "all", label: "Select All" }, ...sdOptions];
    }
    return sdOptions;
  }, [isSearchableMode, options, selectRest.isMulti, showSelectAll, sdOptions]);

  const finalResolvedValue = useMemo(() => {
    if (!isSearchableMode) return resolvedValue;
    if (value === undefined || value === null) return null;
    const isMulti = selectRest.isMulti;

    const resolveSingle = (val) => {
      if (val && typeof val === "object" && "value" in val) return val;
      const cachedLabel = selectedCache[val] || (initialLabel && !Array.isArray(value) ? initialLabel : val);
      if (val !== null && val !== undefined && val !== "") {
        return { value: val, label: String(cachedLabel) };
      }
      return null;
    };

    if (isMulti) {
      if (Array.isArray(value)) return value.map(resolveSingle).filter(Boolean);
      if (typeof value === "string") return value.split(",").map(item => item.trim()).map(resolveSingle).filter(Boolean);
      return [resolveSingle(value)].filter(Boolean);
    }
    return resolveSingle(value);
  }, [isSearchableMode, resolvedValue, value, selectRest.isMulti, selectedCache, initialLabel]);

  return (
    <FormGroup
      className={`form-input-cover ${className} ${isHorizontal ? `grid align-items-center ${isInvalid ? "mb-3" : ""}` : ""
        }`.trim()}
      style={style}
    >
      {/* Label */}
      {labelName && (
        <FormLabel
          className={`text-primary ${isHorizontal ? "g-col-4 m-0" : ""} ${isHidden ? "visually-hidden" : ""
            } ${labelClass}`.trim()}
          id={resolvedId}
          labelName={labelName}
          required={required}
        />
      )}

      {/* Input Wrapper */}
      <div
        className={`${isHorizontal ? "g-col-8" : ""
          }  position-relative ${coverClass}  ${isInvalid && errorMessage ? "mb-3" : ""
          }`.trim()}
      >
        <SelectComponent
          ref={ref}
          className={`custom-form-select ${finalSelectClassName}`}
          classNames={mergedClassNames}
          styles={mergedStyles}
          placeholder={finalPlaceholder}
          name={resolvedName}
          id={resolvedId}
          isDisabled={isDisabled}
          isLoading={isSearchableMode ? sdIsLoading : isLoading}
          isClearable={isClearable}
          isSearchable={isSearchable}
          value={finalResolvedValue}
          onChange={isSearchableMode ? handleSdChange : onChange}
          loadOptions={loadOptions}
          components={mergedComponents}
          options={!loadOptions ? finalOptions : undefined}
          menuPlacement={menuPlacement}
          menuIsOpen={menuIsOpen}
          defaultOptions={loadOptions ? defaultOptions : undefined}
          cacheOptions={loadOptions ? cacheOptions : undefined}
          isCreatable={isCreatable}
          onInputChange={isSearchableMode ? handleSdInputChange : (onInputChange || (() => { }))}
          filterOption={isSearchableMode ? () => true : (selectRest.filterOption ?? (onInputChange && !loadOptions ? () => true : undefined))}
          onMenuScrollToBottom={isSearchableMode ? handleMenuScrollToBottom : selectRest.onMenuScrollToBottom}
          inputValue={isSearchableMode ? inputValue : selectRest.inputValue}
          hideSelectedChips={hideSelectedChips}
          label={label?.name || ""}
          newOption={newOption}
          openMenuOnClick={lookLikeInput ? false : selectRest.openMenuOnClick}
          openMenuOnFocus={lookLikeInput ? false : selectRest.openMenuOnFocus}
          {...selectRest}
        />

        {/* Custom Icon */}
        {icon && (
          <i
            className={`position-absolute top-0 bottom-0 end-0 flex-center ratio-1 bg-primary-10 primary-text rounded-end-4 ${iconClass}`}
            style={{ width: "40px", pointerEvents: "none", zIndex: 5 }}
          >
            {icon}
          </i>
        )}

        {/* Validation Message */}
        {isInvalid && errorMessage && (
          <FormFeedback
            className={`position-absolute top-100 ${validationClass}`}
            message={errorMessage}
          />
        )}
      </div>
    </FormGroup>
  );
};

CustomSelect.displayName = "CustomSelect";

export default forwardRef(CustomSelect);